import { IndexedNotification, UserDefinedNotification } from '@apis/Notification/model';
import { AuthenticationService } from '@root/Services/AuthenticationService';
import { EventEmitter } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import { JobService } from '@root/Services/Jobs/JobService';
import { NotificationViewerService } from '@root/Services/Notification/NotificationViewerService';
import { UserDefinedNotificationApiService } from '@root/Services/Notification/UserDefinedNotificationApiService';
import { queryBuilder } from '@root/Services/QueryExpr';
import { addDays, startOfDay } from 'date-fns';
import { QueryDatasourceCollection } from '@root/Services/Query/QueryDatasourceCollection';
import { BellRinging, Report } from 'tabler-icons-react';
import { inject, singleton } from 'tsyringe';
import { ActivityPoller } from './ActivityPoller';
import { JobStatus, ActivityItemAdapter, ActivityItemModel } from './ActivityTypes';

@singleton()
export class ActivityPanelModel {
    public jobsUpdated = new EventEmitter<JobStatus[]>([]);
    public notificationsUpdated = new EventEmitter<IndexedNotification[]>([]);
    public newJobs = { count: 0, important: false };
    public newNotifs = { count: 0, important: false };
    public toggleRequested = EventEmitter.empty();
    public section: 'Notifications' | 'Activity' = 'Notifications';

    private lastViewed = new Date();
    private disposer = () => {};

    public constructor(
        @inject(JobService) private readonly jobService: JobService,
        @inject(FormatService) private readonly formatSvc: FormatService,
        @inject(ActivityPoller) private readonly activityPoller: ActivityPoller,
        @inject(ActivityItemAdapter) public readonly activityItemAdapter: ActivityItemAdapter,
        @inject(UserDefinedNotificationApiService) private readonly notificationsApi: UserDefinedNotificationApiService,
        @inject(NotificationViewerService) private readonly notificationViewerSvc: NotificationViewerService,
        @inject(AuthenticationService) private readonly authSvc: AuthenticationService,
        @inject(QueryDatasourceCollection) private readonly dsCollection: QueryDatasourceCollection
    ) {
        this.jobService.newJobStarted.listen(this.loadJobs);
        this.notificationViewerSvc.onRead.listen(this.loadNotifications);

        this.seeJobs();
    }

    public open = (section?: 'Notifications' | 'Activity') => {
        this.section = section || 'Activity';
        this.toggleRequested.emit();
    };

    public seeJobs() {
        this.lastViewed = this.formatSvc.toUtc(new Date());
    }

    public poll() {
        this.loadJobs();
        this.notificationsUpdated.emit([]);
        this.loadNotifications();
    }
    public pause() {
        this.disposer();
    }

    public getNotificationDetails() {
        const items = this.notificationsUpdated.value;
        return items.map((item) => this.createNotificationDetail(item));
    }

    private createNotificationDetail(detail: IndexedNotification) {
        const { DatasourceName, OpenedAt, TriggerType } = detail;
        const datasourceMeta = this.dsCollection.getDatasourceMeta(DatasourceName ?? '');
        const seenDescription = OpenedAt ? 'Seen' : 'Unseen';
        const notifType = TriggerType === 'Schedule' ? 'Report' : 'Alert';
        const description = `(${seenDescription}) ${datasourceMeta?.title} ${notifType}`;
        const title = detail.Title || `Untitled ${notifType}`;

        return {
            title,
            description,
            icon: TriggerType === 'Schedule' ? Report : BellRinging,
            showProgress: false,
            visualStatus: OpenedAt ? 'unimportant' : TriggerType === 'Schedule' ? 'success' : 'warning',
            action: {
                description: `View ${TriggerType === 'Schedule' ? 'report' : 'alert'}`,
                execute: async (_, close) => {
                    this.notificationViewerSvc.view(detail.NotificationId ?? 0);
                    close();
                },
            },
            shouldCount: OpenedAt ? false : true,
            notification: detail,
        } as ActivityItemModel & { notification: IndexedNotification };
    }

    private loadJobs = async () => {
        this.disposer?.();
        this.disposer = this.activityPoller.listen((statuses) => {
            const stats = this.getNewJobStatus(statuses);
            this.newJobs = stats;
            if (stats.items.length > 0) {
                this.jobsUpdated.emit(stats.items);
            }
        }).dispose;
    };

    private loadNotifications = async () => {
        const userId = this.authSvc.user?.Id ?? 0;
        const maxAge = startOfDay(addDays(new Date(), -3));

        const notifications = await queryBuilder<Omit<IndexedNotification, 'Date'> & { Date: Date; ['Delivery.InApp.Success']: boolean }>()
            .where((b) => b.and(b.model.RecipientUserId!.eq(userId), b.model.Date!.onOrAfter(maxAge), b.model['Delivery.InApp.Success'].eq(true)))
            .take(30)
            .sortDesc((b) => b.model.Date)
            .execute((q) => this.notificationsApi.queryNotifications(q));

        const result = (notifications.Results ?? []) as unknown as IndexedNotification[];
        this.newNotifs = result.reduce(
            (result, item) => {
                if (!item.OpenedAt) {
                    result.count++;
                    if (item.TriggerType === 'Threshold') {
                        result.important = true;
                    }
                }
                return result;
            },
            { count: 0, important: false } as { count: number; important: boolean }
        );
        this.notificationsUpdated.emit(result);
    };

    private getNewJobStatus(items: JobStatus[]) {
        const result = { count: 0, important: false, items: [] as JobStatus[] };
        for (const item of items) {
            if (item.job.Type !== 'Cloudsaver.Notification.Application.Models.InAppUserDefinedNotification') {
                const info = this.getNewJobInfo(item);
                result.count += info.count;
                result.important ||= info.important;
                result.items.push(item);
            }
        }
        return result;
    }

    private getNewJobInfo(item: JobStatus) {
        const model = this.activityItemAdapter.getActivityModel(item);
        const shouldCount = (item.job.QueuedAt && new Date(item.job.QueuedAt) > this.lastViewed) || !!model.shouldCount;
        return {
            count: shouldCount ? 1 : 0,
            important: (shouldCount && !!item.status.Failed) || model.visualStatus === 'warning',
        };
    }
}

export interface ActivityDetailsProps {
    item: JobStatus;
    displayInfo: ActivityItemModel;
}
